/**
 * Property Groups (Linked Properties) — API service.
 *
 * Wraps the 9 backend endpoints with a consistent axios setup and JWT auth.
 * Throws axios errors on failure — callers should catch them.
 */
import axios from 'axios'
import { SETTINGS } from '@/config/settings'

const base = `${SETTINGS.API.baseUrl}/api/pms/property-groups`

function authHeaders() {
  const token = localStorage.getItem('token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const client = axios.create({
  timeout: SETTINGS.API.timeout || 30000,
})

client.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...authHeaders() }
  return config
})

export default {
  list() {
    return client.get(`${base}/`).then((r) => r.data)
  },
  create({ name, split_method = 'equal', report_format = 'per_property', primary_property_id = '' }) {
    return client
      .post(`${base}/`, { name, split_method, report_format, primary_property_id })
      .then((r) => r.data)
  },
  get(groupId) {
    return client.get(`${base}/${groupId}/`).then((r) => r.data)
  },
  update(groupId, payload) {
    return client.patch(`${base}/${groupId}/`, payload).then((r) => r.data)
  },
  remove(groupId) {
    return client.delete(`${base}/${groupId}/`)
  },
  addMember(groupId, propertyId) {
    return client.post(`${base}/${groupId}/members/`, { property_id: propertyId }).then((r) => r.data)
  },
  removeMember(groupId, propertyId) {
    return client.delete(`${base}/${groupId}/members/${propertyId}/`).then((r) => r.data)
  },
  lock(groupId, year, month) {
    return client.post(`${base}/${groupId}/lock/`, { year, month }).then((r) => r.data)
  },
  unlock(groupId, year, month) {
    return client.post(`${base}/${groupId}/unlock/`, { year, month }).then((r) => r.data)
  },
  lockStatus(groupId, year, month) {
    return client
      .get(`${base}/${groupId}/lock-status/`, { params: { year, month } })
      .then((r) => r.data)
  },
  /**
   * Generate the combined PDF report for a group.
   * payload: { year, month, send_email?, to_email?, subject?, return? }
   * Always asks the backend for `return: 'json'` so we can inspect filename/sent_to
   * without dealing with binary payloads in the test panel.
   */
  generateReport(groupId, payload) {
    return client
      .post(`${base}/${groupId}/generate-report/`, { ...payload, return: 'json' })
      .then((r) => r.data)
  },
  /**
   * Live HTML preview of the combined report. Accepts volatile sidebar values
   * (shared_cleaning_mode/fee, shared_laundry_mode/fee) in the payload so the
   * preview reflects unsaved changes without touching the DB.
   */
  previewHtml(groupId, payload) {
    return client
      .post(`${base}/${groupId}/preview-html/`, payload)
      .then((r) => r.data)
  },
}
